import { EventEmitter, inject, Injectable } from '@angular/core'
import { catchError, of, tap } from 'rxjs'
import { ObservableCacheService } from 'src/app/shared/services/observable-cache.service'
import {
    AssociatedUser,
    BusinessProfileService,
    HandleAccessRequestRequest,
    InstallationsService,
    PrivateProfileService,
    UpdateAssociatedUserRequest,
} from 'src/app/shared/services/backend'

@Injectable({
    providedIn: 'root',
})
export class AdminService {
    private installationsService = inject(InstallationsService)
    private businessProfileService = inject(BusinessProfileService)
    private privateProfileService = inject(PrivateProfileService)
    private cache = inject(ObservableCacheService)

    // Cache configuration
    private readonly CACHE_DURATION_MS = 15 * 60 * 1000 // 15 minutes

    private updateAccessRequestsEvent = new EventEmitter()

    public getAccessRequest$(customerNumber: string, forceRefresh: boolean = false) {
        const cacheKey = this.cache.createKey('accessRequests', customerNumber)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.installationsService.apiInstallationsAccessRequestsCustomerNumberGet(customerNumber).pipe(
                    catchError(() => {
                        this.cache.clear(cacheKey)
                        return of([])
                    })
                ),
            { forceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears access requests cache for a specific customer
     */
    public clearAccessRequestsCache(customerNumber: string): void {
        const cacheKey = this.cache.createKey('accessRequests', customerNumber)
        this.cache.clear(cacheKey)
    }

    /**
     * Clears all access requests cache entries
     */
    public clearAllAccessRequestsCache(): void {
        this.cache.clearByPattern(/^accessRequests:/)
    }

    public acceptRequest(requestId: string, customerNumber?: string) {
        return this.installationsService
            .apiInstallationsAccessRequestPost({
                action: 'ACCEPT',
                requestId: requestId,
            } as HandleAccessRequestRequest)
            .pipe(
                tap((_) => {
                    // Invalidate access requests cache
                    if (customerNumber) {
                        this.clearAccessRequestsCache(customerNumber)
                    } else {
                        this.clearAllAccessRequestsCache()
                    }
                    this.updateAccessRequestsEvent.emit()
                })
            )
    }

    public rejectRequest(requestId: string, customerNumber?: string) {
        return this.installationsService
            .apiInstallationsAccessRequestPost({
                action: 'REJECT',
                requestId: requestId,
            } as HandleAccessRequestRequest)
            .pipe(
                tap((_) => {
                    // Invalidate access requests cache
                    if (customerNumber) {
                        this.clearAccessRequestsCache(customerNumber)
                    } else {
                        this.clearAllAccessRequestsCache()
                    }
                    this.updateAccessRequestsEvent.emit()
                })
            )
    }

    public getAssociatedUser$(customerNumber: string, isBusinessCustomer: boolean, forceRefresh: boolean = false) {
        const cacheKey = this.cache.createKey('associatedUsers', customerNumber, isBusinessCustomer)

        if (!customerNumber) {
            return of({ associatedUsers: null })
        }

        return this.cache.getOrSet(
            cacheKey,
            () => {
                if (isBusinessCustomer) {
                    return this.businessProfileService
                        .apiProfileBusinessCustomerCustomerNumberUsersGet(customerNumber)
                        .pipe(
                            catchError(() => {
                                this.cache.clear(cacheKey)
                                return of({ associatedUsers: null })
                            })
                        )
                } else {
                    return this.privateProfileService
                        .apiProfilePrivateCustomerCustomerNumberUsersGet(customerNumber)
                        .pipe(
                            catchError(() => {
                                this.cache.clear(cacheKey)
                                return of({ associatedUsers: null })
                            })
                        )
                }
            },
            { forceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears associated users cache for a specific customer
     */
    public clearAssociatedUsersCache(customerNumber: string, isBusinessCustomer?: boolean): void {
        if (isBusinessCustomer !== undefined) {
            const cacheKey = this.cache.createKey('associatedUsers', customerNumber, isBusinessCustomer)
            this.cache.clear(cacheKey)
        } else {
            // Clear both business and private if not specified
            this.cache.clearByPattern(new RegExp(`^associatedUsers:${customerNumber}:`))
        }
    }

    /**
     * Clears all associated users cache entries
     */
    public clearAllAssociatedUsersCache(): void {
        this.cache.clearByPattern(/^associatedUsers:/)
    }

    public updateAssociatedUser(customerNumber: string, user: AssociatedUser, isBusinessCustomer?: boolean) {
        const response = this.businessProfileService.apiProfileBusinessCustomerCustomerNumberUserPut(customerNumber, {
            mitId: user.mitId,
            endDate: user.endDate,
            isAdmin: user.isAdmin,
            email: user.email,
            name: user.name,
        } as UpdateAssociatedUserRequest)

        return response.pipe(
            tap(() => {
                // Invalidate associated users cache after successful update
                this.clearAssociatedUsersCache(customerNumber, isBusinessCustomer)
            })
        )
    }

    public onAccessRequestsUpdated(callback: () => void) {
        this.updateAccessRequestsEvent.subscribe(callback)
    }
}
